import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { useMemo, useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { QueryClient, useQuery, useQueryClient } from 'react-query';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import { useAsyncDownloadDocumentV2 } from '../../hooks/downloadDocument';
import { useFetchRentStepByDate } from '../../hooks/data-fetching/useFetchLeaseRentSteps';
import useAbortController from '../../hooks/useAbortController';
import { isHouseholdMiMoDateChangesFlagEnabled } from '../ResidentProfile/hooks';

import {
  filterOutFosterChildAdults,
  getSettingsByPropertyAndProgram,
  sortCertificationsByFloorPlanChanges,
} from './utils';
import HouseholdProfileService from '../../services/householdProfileService';
import StateAffordableProgramConfigService from '../../services/stateAffordableProgramConfigService';
import UnitService from '../../services/unitService';
import AffordableService from '../../services/affordableService';

import {
  MOVE_IN_CERT_TYPE,
  HUD_PROGRAM_NAME,
  PH_PROGRAM_NAME,
  RECERT_CERT_TYPE,
  INITIAL_CERT_TYPE,
  INITIAL_UT_CERT_TYPE,
  LIHTC_PROGRAM_NAME,
  RD_PROGRAM_NAME,
  HOME_PROGRAM_NAME,
  CITC_PROGRAM_NAME,
  LIHTC_HOME_PROGRAM_NAME,
  PARTIAL_CERTIFICATION_TYPES,
  UNIT_TRANSFER_CERT_TYPE,
  determineCertTypeNameDefinitions,
} from './constants';
import {
  filterHouseholdMembers,
  filterAndSortHouseholdMembersWithMinors,
} from './utils';
import {
  GROSS_RENT_CERT_TYPE,
  RD_GROSS_RENT_CERT_TYPE,
} from '../../constants/affordableCertificationTypes';
import { AFFORDABLE_QUALIFICATION_STATUSES } from '../../utils/affordable';
import ApplicationService from '../../services/applicationService';
import {
  CERT_TAB,
  enrichHouseholdQualsWithResidentCompletedTabData,
  mapAffordableQualificationsForCertTab,
  qualRowForTab,
} from './isHudCompletedTab';
import { TAB_INDEX } from './CertificationStatusTabs/CertificationStatusTabs.constants';
import {
  EMPTY_AFFORDABLE_QUALIFICATIONS_LIST,
  EMPTY_AFFORDABLE_SETUP,
  EMPTY_HOUSEHOLD_AFFORDABLE_DETAILS,
  EMPTY_PREVIOUS_ASSISTANCE_DETAILS,
  EMPTY_QUALIFICATION_DATA,
  EMPTY_RESIDENT_AFFORDABLE_QUALIFICATIONS,
} from './stableEmptyDefaults';

/** Stable defaults for useMemo/useEffect deps — see stableEmptyDefaults.js (ENG-28187 / HUD reopen). */
export {
  EMPTY_AFFORDABLE_QUALIFICATIONS_LIST,
  EMPTY_AFFORDABLE_SETUP,
  EMPTY_HOUSEHOLD_AFFORDABLE_DETAILS,
  EMPTY_PREVIOUS_ASSISTANCE_DETAILS,
  EMPTY_QUALIFICATION_DATA,
  EMPTY_QUALIFICATION_OPTIONS,
  EMPTY_RESIDENT_AFFORDABLE_QUALIFICATIONS,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
} from './stableEmptyDefaults';

// formName: the document a Move-In/Move-Out Date change flag asks the user to regenerate
// (ENG-32978, ENG-32980).
type NeedsReviewReason = {
  id: string,
  triggeringEvent: string,
  formName?: ?string,
};

// The Needs Review triggers of the household-mi-mo-date-changes lane: each shows its own badge.
const DATE_CHANGE_NEEDS_REVIEW_EVENTS: $ReadOnlyArray<string> = [
  'MOVE_IN_DATE_CHANGE',
  'MOVE_OUT_DATE_CHANGE',
];

const pickNeedsReviewBadgeReason = (
  row: Object,
  flagCertsForReview: boolean,
  isMiMoDateChangesEnabled: boolean,
): NeedsReviewReason | null => {
  let reasons: Array<NeedsReviewReason> = [];
  if (Array.isArray(row?.needsReviewReasons)) {
    reasons = row.needsReviewReasons;
  } else if (row?.needsReviewId) {
    reasons = [
      {
        id: row.needsReviewId,
        triggeringEvent: row.needsReviewTriggeringEvent,
      },
    ];
  }
  if (flagCertsForReview) {
    return reasons[0] ?? null;
  }
  if (!isMiMoDateChangesEnabled) {
    return null;
  }
  return (
    reasons.find(({ triggeringEvent }) =>
      DATE_CHANGE_NEEDS_REVIEW_EVENTS.includes(triggeringEvent),
    ) ?? null
  );
};

/**
 * Returns a copy of a certification row whose needsReviewId/needsReviewTriggeringEvent are the
 * Needs Review reason its badge shows (null/null for no badge), with needsReviewFormName naming the
 * document a Move-In/Move-Out Date change flag asks the user to regenerate. The general cert-review
 * flag shows the first unresolved reason (the backend lists non-date-change reasons first, then
 * oldest). With it off, only a Move-In or Move-Out Date change reason shows (the first one listed),
 * and only under household-mi-mo-date-changes; a cert can carry a date-change reason and another
 * reason at once, so the date-change one is searched for rather than read off the backend's single
 * chosen reason. Rows without needsReviewReasons fall back to their
 * needsReviewId/needsReviewTriggeringEvent pair. Never mutates the row, so a later flag change can
 * still read the original reasons from the same cached response.
 */
export const withNeedsReviewBadgeReason = (
  row: Object,
  {
    flagCertsForReview,
    isMiMoDateChangesEnabled,
  }: { flagCertsForReview: boolean, isMiMoDateChangesEnabled: boolean },
): Object => {
  const reason = pickNeedsReviewBadgeReason(
    row,
    flagCertsForReview,
    isMiMoDateChangesEnabled,
  );
  return {
    ...row,
    needsReviewId: reason?.id ?? null,
    needsReviewTriggeringEvent: reason?.triggeringEvent ?? null,
    needsReviewFormName: reason?.formName ?? null,
  };
};

/**
 * Invalidates the common affordable qualification queries so the UI reflects
 * the latest data after mutations.
 * getHouseholdProfileAffordableQualificationsByProgram:
 *  Query that loads all the affordable qualifications for the household and program.
 * getCompletionStatusesAndAssistanceDetails:
 *  Query that loads the completion statuses and assistance details for the current qualification (HUD, RD and PH only).
 * getAffordableQualificationCompletionStatuses:
 *  Query that loads the completion status for each step of the certification process for the current qualification.
 *
 * @param {QueryClient} queryClient React Query client instance used to invalidate queries.
 */
export const invalidateAffordableQualificationQueries = (
  queryClient: QueryClient,
) => {
  const commonQueryKeys = [
    'getHouseholdProfileAffordableQualificationsByProgram',
    'getCompletionStatusesAndAssistanceDetails',
    'getAffordableQualificationCompletionStatuses',
  ];

  commonQueryKeys.forEach((key) => {
    queryClient.invalidateQueries([key]);
  });
};

export const invalidateHudReopenRollbackQueries = (
  queryClient: QueryClient,
) => {
  invalidateAffordableQualificationQueries(queryClient);
  queryClient.invalidateQueries([
    'affordableCitizenStatusService.getAllByHousehold',
  ]);
  queryClient.invalidateQueries([
    'affordableDeductionService.getAllByHousehold',
  ]);
  queryClient.invalidateQueries(['applicantAffordableCommunityServiceService']);
  queryClient.invalidateQueries(['fetchDataForIncomeAssetForm']);
  queryClient.invalidateQueries(['getFormInstances']);
  queryClient.invalidateQueries(['getFormTemplates']);
  queryClient.invalidateQueries(['getQualificationHistory']);
  queryClient.invalidateQueries(['useCertificationsQuery']);
};

export const getRecertDueDate = (
  recerts: Array<Object>,
  affordableProgram: string,
): string => {
  if (!recerts || recerts.length === 0) return '';
  return recerts.reduce((acc, program) => {
    const programName = program?.pap?.map?.name ?? '';
    if (programName === affordableProgram) {
      return program.recertDueDate;
    }
    return acc;
  }, '');
};

export const getCompleteCertificationRecertDueDate = ({
  programName,
  qualificationRecertDueDate,
  householdRecertDueDate,
}: {
  programName: string,
  qualificationRecertDueDate?: string | null,
  householdRecertDueDate: string,
}): string => {
  if (programName !== PH_PROGRAM_NAME && qualificationRecertDueDate) {
    return qualificationRecertDueDate;
  }

  return householdRecertDueDate ?? '';
};

/**
 * The key is buried deep, so a helper to recreate it elsewhere for updates
 * @param organizationId
 * @param propertyId
 * @param householdId
 * @param propertyAffordableProgramId
 * @returns {(string|*)[]}
 */
export const makeHouseholdProfileAffordableQualificationsByProgramQueryKey = ({
  organizationId,
  propertyId,
  householdId,
  propertyAffordableProgramId,
}): Array<string> => [
  'getHouseholdProfileAffordableQualificationsByProgram',
  organizationId,
  propertyId,
  householdId,
  propertyAffordableProgramId,
];

/**
 * Assuming the array is sorted, returns the element previous to current
 * @param currentAffordableQualification
 * @param affordableQualifications
 * @returns {Object|{}}
 */
const getPreviousAffordableQualification = (
  currentAffordableQualification: Object,
  affordableQualifications: Array<Object>,
): Object => {
  if (!affordableQualifications) return {};
  const loc = affordableQualifications.indexOf(currentAffordableQualification);
  if (loc !== -1 && loc < affordableQualifications.length + 1) {
    return affordableQualifications[loc + 1];
  }
  return {};
};

const useHouseholdProfileAffordableQualificationsByProgramQuery = ({
  organizationId,
  propertyId,
  householdId,
  propertyAffordableProgramId,
  options,
}) => {
  const householdProfileService = useMemo(
    () => new HouseholdProfileService(),
    [],
  );
  const queryKey =
    makeHouseholdProfileAffordableQualificationsByProgramQueryKey({
      organizationId,
      propertyId,
      householdId,
      propertyAffordableProgramId,
    });

  return useQuery(
    queryKey,
    () =>
      householdProfileService.getHouseholdProfileAffordableQualificationsByProgram(
        organizationId,
        propertyId,
        householdId,
        propertyAffordableProgramId,
      ),
    {
      ...options,
      refetchOnRefocus: false,
      refetchOnWindowFocus: false,
    },
  );
};

const useGetQualificationHistory = ({
  organizationId,
  propertyId,
  applicationId,
  options = {},
}) => {
  const applicationService = useMemo(() => new ApplicationService(), []);

  const queryKey = [
    'getQualificationHistory',
    organizationId,
    propertyId,
    applicationId,
  ];

  return useQuery(
    queryKey,
    () =>
      applicationService.getQualificationHistory(
        organizationId,
        propertyId,
        applicationId,
      ),
    {
      ...options,
      refetchOnRefocus: false,
      refetchOnWindowFocus: false,
    },
  );
};

const useAffordableQualifications = ({
  organizationId,
  propertyId,
  programName,
  householdId,
  propertyAffordableProgramId,
  leases,
  targetQualificationId,
  certificationTabIndex,
  residentAffordableQualifications = EMPTY_RESIDENT_AFFORDABLE_QUALIFICATIONS,
}) => {
  const flags = useFlags();
  const { flagCertsForReview } = flags;
  const isMiMoDateChangesEnabled = isHouseholdMiMoDateChangesFlagEnabled(flags);

  const { data, isLoading, refetch, isFetching } =
    useHouseholdProfileAffordableQualificationsByProgramQuery({
      organizationId,
      propertyId,
      householdId,
      propertyAffordableProgramId,
      options: {
        enabled: Boolean(
          organizationId &&
            propertyId &&
            householdId &&
            propertyAffordableProgramId,
        ),
      },
    });

  const {
    affordableQualifications,
    affordableQualificationId,
    affordableQualificationCorrections,
    affordableSetup,
    assistanceDetails,
    backgroundAtAdmission,
    exemptions,
    familySelfSufficiency,
    hasUnit,
    householdAffordableDetails,
    householdMoveInDate,
    householdMembers,
    householdMembersWithMinors,
    householdPhaseInReliefs,
    leasedRent,
    leaseId,
    qualificationUnitId,
    masterAffordableProgramId,
    masterAffordableProgramName,
    previousAssistanceDetails,
    qualificationData,
    isTestHouseholdCurrent,
    qualificationOptions,
    residentHouseholdMembers,
    stateProgramConfigs,
    canStartInitialCertification,
  } = useMemo(() => {
    const {
      affordableQualifications: rawAffordableQualifications,
      affordableSetUp,
      currentHouseholdMembers,
      stateProgramConfigs,
      householdAffordableDetails,
      householdMoveInDate,
      canStartInitialCertification,
      householdPhaseInReliefs,
    } = data || {};

    const enrichedAffordableQualifications =
      enrichHouseholdQualsWithResidentCompletedTabData(
        rawAffordableQualifications ?? EMPTY_AFFORDABLE_QUALIFICATIONS_LIST,
        residentAffordableQualifications,
      );

    const affordableQualifications = enrichedAffordableQualifications?.map(
      (x) =>
        withNeedsReviewBadgeReason(x, {
          flagCertsForReview,
          isMiMoDateChangesEnabled,
        }),
    );

    const filteredAffordableQualifications = affordableQualifications?.filter(
      ({ qualificationData: { isActive, recertificationStarted } }) =>
        isActive || recertificationStarted,
    );

    const inActiveAffordableQualifications = affordableQualifications?.filter(
      ({ qualificationData: { isActive, recertificationStarted } }) =>
        !isActive && !recertificationStarted,
    );

    const activeOrInactiveAffordableQualifications =
      filteredAffordableQualifications?.length
        ? filteredAffordableQualifications
        : inActiveAffordableQualifications;

    const canSelectAsCurrentQualification = (qualificationData) =>
      qualificationData?.certificationStatus !== 'REOPEN' &&
      qualificationData?.isCorrection !== true;

    // still need to get the id for inactive qualifications if an active one doesn't exist
    let affordableQualificationId =
      activeOrInactiveAffordableQualifications?.find(
        ({ masterAffordableProgramName, qualificationData }) =>
          masterAffordableProgramName === programName &&
          canSelectAsCurrentQualification(qualificationData) &&
          ![RD_GROSS_RENT_CERT_TYPE, GROSS_RENT_CERT_TYPE].includes(
            qualificationData?.certificationType,
          ),
      )?.qualificationData?.id || null;

    let currentAffordableQualification = {};

    if (!targetQualificationId) {
      currentAffordableQualification =
        filteredAffordableQualifications?.find(
          ({
            qualificationData: {
              isActive,
              certificationStatus,
              isCorrection,
              certificationType,
            },
          }) =>
            isActive &&
            canSelectAsCurrentQualification({
              certificationStatus,
              isCorrection,
            }) &&
            ![RD_GROSS_RENT_CERT_TYPE, GROSS_RENT_CERT_TYPE].includes(
              certificationType,
            ),
        ) ?? {};
    } else {
      const rootRow =
        (affordableQualifications ?? []).find(
          (q) =>
            String(q?.qualificationData?.id) === String(targetQualificationId),
        ) ?? {};

      const useFrozenCompletedTabRow =
        certificationTabIndex === TAB_INDEX.COMPLETED &&
        Boolean(rootRow?.completedTabData);

      currentAffordableQualification = useFrozenCompletedTabRow
        ? qualRowForTab(rootRow, CERT_TAB.COMPLETED)
        : rootRow;

      affordableQualificationId = targetQualificationId;
    }

    const {
      affordableQualificationCorrections,
      eventHouseholdMembers,
      exceptions: exemptions,
      assistanceDetails,
      qualificationData,
      isTestHouseholdCurrent,
      masterAffordableProgramId,
      masterAffordableProgramName,
      unitId,
      backgroundAtAdmission,
      familySelfSufficiency,
    } = currentAffordableQualification;

    const previousAffordableQualification = getPreviousAffordableQualification(
      currentAffordableQualification,
      affordableQualifications,
    );
    const previousAssistanceDetails =
      previousAffordableQualification?.assistanceDetails ??
      EMPTY_PREVIOUS_ASSISTANCE_DETAILS;

    // find correct lease to pull rent steps from
    const filteredLeases = Array.isArray(leases)
      ? leases
          .filter((lease) => {
            const voucherDate = qualificationData?.voucherEffectiveDate
              ? moment(qualificationData.voucherEffectiveDate)
                  .startOf('day')
                  .toDate()
              : null;
            const startDate = lease.startDate
              ? moment(lease.startDate).startOf('day').toDate()
              : null;
            const endDate = lease.endDate ? new Date(lease.endDate) : null;

            if (!voucherDate || !startDate) return false;

            // If lease has endDate, check if voucherDate is within [startDate, endDate]
            if (endDate) {
              return voucherDate >= startDate && voucherDate <= endDate;
            }
            // If no endDate, check if voucherDate is after startDate
            return voucherDate >= startDate;
          })
          .sort(
            (a, b) =>
              new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
          )
      : [];

    const firstLeaseMatchingVoucherDate =
      filteredLeases.length > 0 ? filteredLeases[0] : null;
    const householdMembers = filterHouseholdMembers(eventHouseholdMembers);

    const householdMembersWithMinors = filterAndSortHouseholdMembersWithMinors(
      eventHouseholdMembers,
    );

    const qualificationOptions = filteredAffordableQualifications?.map(
      ({
        qualificationData,
        tenantEventData,
        masterAffordableProgramName,
        activeLeaseUnitId,
        certUnitNumber,
      }) => {
        const isReopen = qualificationData?.certificationStatus === 'REOPEN';
        const isCorrection = qualificationData?.isCorrection;
        const isReopenOrCorrection = isReopen || isCorrection;
        let text = determineCertTypeNameDefinitions(
          masterAffordableProgramName,
        )[qualificationData?.certificationType];
        // Get from tenantEventData if available
        // otherwise use certUnitNumber which comes from the lease matching cert date
        const unitNumber =
          tenantEventData?.tenantEventUnit?.unitNumber || certUnitNumber;
        const certUnitId = tenantEventData?.tenantEventUnit?.unitId;
        // assumption is that some unit transfer occured for this household
        const certUnitDifferent = activeLeaseUnitId !== certUnitId;
        if (isReopenOrCorrection && unitNumber && certUnitDifferent) {
          // prepend the unit number for this cert label
          text = `Unit ${unitNumber} - ${text}`;
        }
        return {
          text,
          value: qualificationData?.id,
        };
      },
    );

    return {
      affordableQualifications:
        affordableQualifications ?? EMPTY_AFFORDABLE_QUALIFICATIONS_LIST,
      affordableQualificationId,
      affordableQualificationCorrections,
      affordableSetup: affordableSetUp ?? EMPTY_AFFORDABLE_SETUP,
      assistanceDetails,
      backgroundAtAdmission,
      exemptions,
      familySelfSufficiency,
      hasUnit: Boolean(unitId?.length),
      householdAffordableDetails:
        householdAffordableDetails ?? EMPTY_HOUSEHOLD_AFFORDABLE_DETAILS,
      householdPhaseInReliefs,
      householdMoveInDate,
      masterAffordableProgramId,
      masterAffordableProgramName,
      qualificationOptions,
      householdMembers,
      householdMembersWithMinors,
      leasedRent:
        firstLeaseMatchingVoucherDate?.leasedRent ||
        currentAffordableQualification?.leasedRent ||
        // Pre-assignment (no lease/unit): fall back to the floor plan's quoting rent
        // surfaced on qualificationData by the backend.
        qualificationData?.floorPlanQuotingRent,
      leaseId:
        firstLeaseMatchingVoucherDate?.id ||
        currentAffordableQualification?.activeLeaseId,
      previousAssistanceDetails,
      qualificationUnitId:
        currentAffordableQualification?.tenantEventData?.tenantEventUnit
          ?.unitId ||
        unitId ||
        null,
      qualificationData: qualificationData || EMPTY_QUALIFICATION_DATA,
      isTestHouseholdCurrent,
      residentHouseholdMembers: currentHouseholdMembers,
      stateProgramConfigs,
      canStartInitialCertification,
    };
  }, [
    data,
    targetQualificationId,
    leases,
    flagCertsForReview,
    isMiMoDateChangesEnabled,
    programName,
    certificationTabIndex,
    residentAffordableQualifications,
  ]);

  return {
    affordableQualifications,
    affordableQualificationId,
    affordableQualificationCorrections,
    affordableSetup,
    assistanceDetails,
    backgroundAtAdmission,
    exemptions,
    familySelfSufficiency,
    hasUnit,
    householdAffordableDetails,
    householdPhaseInReliefs,
    householdMoveInDate,
    masterAffordableProgramId,
    masterAffordableProgramName,
    qualificationOptions,
    qualificationData,
    isTestHouseholdCurrent,
    householdMembers,
    householdMembersWithMinors,
    residentHouseholdMembers,
    leasedRent,
    leaseId,
    previousAssistanceDetails,
    qualificationUnitId,
    isLoading,
    stateProgramConfigs,
    refetch,
    canStartInitialCertification,
    isFetching,
  };
};

// correctedQualId now points at the immediate parent correction (a chain: C -> B -> A),
// not the family root, so resolving the Completed-tab dedupe key requires walking it up
// rather than reading one hop. The column is self-referential with no DB-level cycle
// guard, so track visited ids and bail out if one repeats instead of looping forever.
//
// An intermediate link can be missing from qualificationsById: the backend's
// dedupeQualificationHistories (AffordableQualificationHistories.js) drops every row whose
// id appears as some other row's correctedQualId, which under a chain removes A and B and
// leaves only C. So the walk can run off the end of what we actually hold — when it does,
// stop and return the furthest ancestor reached rather than guess further or return
// undefined. Callers use the result as a grouping key, so it must always be a string.
export const resolveFamilyRootId = (
  id: string,
  qualificationsById: Map<string, Object>,
): string => {
  const visited = new Set();
  let current = id;
  while (current && !visited.has(current)) {
    visited.add(current);
    const parentId =
      qualificationsById.get(current)?.qualificationData?.correctedQualId;
    if (!parentId) return current;
    current = parentId;
  }
  return current;
};

const useGeneralAffordable = ({
  householdId,
  propertyId: explicitPropertyId,
  programName,
  propertyAffordableProgramId,
  recertDueDates,
  refetchProfile = () => {},
  residentId,
  tabKey,
  leases,
  applicationId,
  targetQualificationId,
  certificationTabIndex,
  residentAffordableQualifications = EMPTY_RESIDENT_AFFORDABLE_QUALIFICATIONS,
}: {
  householdId: string,
  propertyId?: string,
  propertyAffordableProgramId: string,
  recertDueDates: string[],
  residentId: string,
  leases: Array<Object>,
  certificationTabIndex?: number,
  residentAffordableQualifications?: Array<Object>,
}) => {
  const queryClient = useQueryClient();
  const {
    initialCertsForLihtc,
    floorPlanChanges,
    affordableWaitlistV2: waitlistRestrictionsEnabled = false,
  } = useFlags();
  const selectedProperty = useSelectedProperty();
  const {
    organizationId,
    id: selectedPropertyId,
    propertyClass,
  } = selectedProperty || {};
  const propertyId = explicitPropertyId || selectedPropertyId;
  const resolvedProperty = useMemo(() => {
    return explicitPropertyId && explicitPropertyId !== selectedPropertyId
      ? { ...selectedProperty, id: explicitPropertyId }
      : selectedProperty;
  }, [explicitPropertyId, selectedPropertyId, selectedProperty]);
  const limitRecertStart = selectedProperty?.limitRecertStart ?? true;
  const isAffordableMixedProperty = ['Mixed', 'Affordable'].includes(
    propertyClass?.name,
  );
  const isResident = Boolean(residentId);
  const {
    affordableQualifications,
    affordableQualificationId,
    affordableQualificationCorrections,
    affordableSetup,
    assistanceDetails,
    backgroundAtAdmission,
    exemptions,
    familySelfSufficiency,
    hasUnit,
    householdAffordableDetails,
    householdMoveInDate,
    householdMembers,
    householdMembersWithMinors,
    householdPhaseInReliefs,
    leasedRent,
    leaseId,
    qualificationUnitId,
    masterAffordableProgramId,
    previousAssistanceDetails,
    qualificationData,
    isTestHouseholdCurrent,
    qualificationOptions,
    residentHouseholdMembers,
    isLoading,
    stateProgramConfigs,
    refetch,
    canStartInitialCertification,
    isFetching,
  } = useAffordableQualifications({
    organizationId,
    programName,
    propertyId,
    householdId,
    propertyAffordableProgramId,
    leases,
    targetQualificationId,
    certificationTabIndex,
    residentAffordableQualifications,
  });

  // Fetch unit by ID when AQ has unitId but API didn't return unit object (e.g. reopened cert with different unit).
  const unitService = useMemo(() => new UnitService(), []);
  const { data: fetchedQualificationUnit } = useQuery(
    ['getUnitById', organizationId, propertyId, qualificationUnitId],
    () => unitService.getById(propertyId, organizationId, qualificationUnitId),
    {
      enabled: Boolean(qualificationUnitId && organizationId && propertyId),
      refetchOnWindowFocus: false,
    },
  );

  const qualificationUnitResolved = fetchedQualificationUnit || null;

  const { stepByDate } = useFetchRentStepByDate({
    leaseId,
    date: qualificationData?.voucherEffectiveDate,
  });

  const rentAtDate = stepByDate?.amount ?? leasedRent;

  const state = selectedProperty?.state.id;
  const { data: hotmaStatus } = useFetchStateProgramHotmaStatus({
    programName,
    state,
    effectiveDate: qualificationData?.voucherEffectiveDate,
    options: {
      enabled: Boolean(
        programName && state && qualificationData?.voucherEffectiveDate,
      ),
    },
  });

  const hasAdoptedHotma = hotmaStatus?.hasAdoptedHOTMA ?? false;

  // refetch qualification data when tab changes
  // refetch activeVoucher in case today is 1st day of the month and a new voucher is created
  useEffect(() => {
    if (
      [
        LIHTC_PROGRAM_NAME,
        HUD_PROGRAM_NAME,
        RD_PROGRAM_NAME,
        HOME_PROGRAM_NAME,
        CITC_PROGRAM_NAME,
        LIHTC_HOME_PROGRAM_NAME,
      ].includes(tabKey)
    ) {
      queryClient.invalidateQueries([
        'getCompletionStatusesAndAssistanceDetails',
      ]);
      queryClient.invalidateQueries([
        'getAffordableQualificationCompletionStatuses',
      ]);
      queryClient.invalidateQueries(['getCurrentActiveVoucher']);
      refetch();
    }
  }, [queryClient, refetch, tabKey]);

  useEffect(() => {
    refetchProfile();
    // refetchProfile not added to dependency array because it causes an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualificationData?.id]);

  const {
    affordableDocuments: affordableDocumentIds,
    certificationNotes,
    certificationStatus,
    certificationType,
    complianceApprovalId,
    complianceNotes,
    correctionReasonIds,
    hasGeneratedTIC,
    hasGeneratedHUD58,
    reCompleteCertificationStarted = false,
    recertificationStarted,
    specialNeedsDesignationIds,
    voucherEffectiveDate,
    homeRecertTypeId,
  } = qualificationData;

  const { data: qualificationHistory, isLoading: isLoadingHistory } =
    useGetQualificationHistory({
      organizationId,
      propertyId,
      householdId,
      applicationId,
    });

  const completedAffordableQualifications = useMemo(() => {
    const list = affordableQualifications ?? [];

    const historyIds = new Set(
      (qualificationHistory ?? [])
        .map((h) => h?.affordableQualificationId ?? h?.aq?.id)
        .filter(Boolean),
    );

    const filtered = list.filter((q) =>
      historyIds.has(q?.qualificationData?.id),
    );

    // Keep a single canonical row per family (program + effective date + cert type),
    // matching backend retroactive auto-start family dedupe semantics.
    //
    // voidedAt joins the key so a voided certification never shares a family with the one that
    // replaced it. A replacement GR started for a voided GR's effective date matches on all three
    // of the original segments, and wins the recency tie-break, so without this the voided row
    // disappears the moment the replacement completes (ENG-32454 scenario 7). A null voidedAt
    // leaves the key exactly as it was, so nothing changes for certifications that were never
    // voided.
    const getFamilyKey = (q) => {
      const certificationData = q?.qualificationData;
      return `${q?.propertyAffordableProgramId ?? ''}|${String(
        certificationData?.voucherEffectiveDate ?? '',
      )}|${certificationData?.certificationType ?? ''}|${String(
        certificationData?.voidedAt ?? '',
      )}`;
    };

    const getFamilyRecencyValue = (q) => {
      const certificationData = q?.qualificationData;
      return (
        certificationData?.updatedAt ??
        certificationData?.createdAt ??
        certificationData?.certificationCompletionDate ??
        ''
      );
    };

    const familyWinners = filtered.reduce((acc, q) => {
      const familyKey = getFamilyKey(q);
      const prev = acc[familyKey];
      const prevValue = getFamilyRecencyValue(prev);
      const curValue = getFamilyRecencyValue(q);
      if (!prev || curValue > prevValue) acc[familyKey] = q;
      return acc;
    }, {});

    const filteredByFamily = Object.values(familyWinners);

    // Deduplicate only completed corrections from the same original AQ chain.
    // This keeps Completed less restrictive for in-flight/non-completed rows.
    // The dedupe key is the family root (not the immediate parent) so a chain
    // of corrections (C corrects B corrects A) collapses to one Completed row
    // instead of one per hop -- see resolveFamilyRootId above.
    const qualificationsById = new Map(
      filteredByFamily
        .filter((q) => q?.qualificationData?.id)
        .map((q) => [q.qualificationData.id, q]),
    );
    const getCompletedCorrectionKey = (q) => {
      const certificationData = q?.qualificationData;
      const cqId = certificationData?.correctedQualId;
      const isCorrection = certificationData?.isCorrection === true;
      const isCompleted =
        certificationData?.certificationStatus ===
          AFFORDABLE_QUALIFICATION_STATUSES.COMPLETE ||
        Boolean(certificationData?.certificationCompletionDate);
      return isCorrection && cqId && isCompleted
        ? resolveFamilyRootId(cqId, qualificationsById)
        : null;
    };

    const getCorrectionRecencyValue = (q) => {
      const certificationData = q?.qualificationData;
      return (
        certificationData?.certificationCompletionDate ??
        certificationData?.updatedAt ??
        certificationData?.createdAt ??
        ''
      );
    };

    const winnerByCorrectedId = filteredByFamily.reduce((acc, q) => {
      const dedupeKey = getCompletedCorrectionKey(q);
      if (!dedupeKey) return acc;
      const prev = acc[dedupeKey];
      const prevDate = getCorrectionRecencyValue(prev);
      const curDate = getCorrectionRecencyValue(q);
      if (!prev || curDate > prevDate) acc[dedupeKey] = q;
      return acc;
    }, {});

    const suppressedIds = new Set([
      ...Object.keys(winnerByCorrectedId), // original AQs superseded by a correction
      ...filteredByFamily // losing corrections (not the most recent one for their original)
        .filter((q) => {
          const dedupeKey = getCompletedCorrectionKey(q);
          return dedupeKey && winnerByCorrectedId[dedupeKey] !== q;
        })
        .map((q) => q?.qualificationData?.id),
    ]);

    const deduplicatedList = filteredByFamily.filter(
      (q) => !suppressedIds.has(q?.qualificationData?.id),
    );

    const completed = floorPlanChanges
      ? deduplicatedList.sort(sortCertificationsByFloorPlanChanges)
      : deduplicatedList;

    return mapAffordableQualificationsForCertTab(completed, CERT_TAB.COMPLETED);
  }, [affordableQualifications, qualificationHistory, floorPlanChanges]);

  const handleDownloadDocument = useAsyncDownloadDocumentV2(
    propertyId,
    organizationId,
  );

  // isModification is true if the program is RD and the qualification is a correction
  const isModification =
    programName === RD_PROGRAM_NAME && qualificationData?.isCorrection;

  // Partial certs (Gross Rent, Unit Transfer, Assign/Remove RA, Co-Tenant to Tenant) carry the
  // prior full cert's determination forward and never re-verify the household, so they require no
  // recertification form -- the next full cert does that. Previously only Unit Transfer was
  // excluded, so a Gross Rent demanded a recert form its members had no way to have completed,
  // leaving the Household Member(s) section permanently incomplete.
  const showRecertForm = useMemo(
    () =>
      isResident &&
      !isModification &&
      !reCompleteCertificationStarted &&
      ![
        MOVE_IN_CERT_TYPE,
        ...(waitlistRestrictionsEnabled
          ? PARTIAL_CERTIFICATION_TYPES
          : [UNIT_TRANSFER_CERT_TYPE]),
      ].includes(certificationType),
    [
      certificationType,
      waitlistRestrictionsEnabled,
      isModification,
      isResident,
      reCompleteCertificationStarted,
    ],
  );

  const isHouseholdMembersChecklistFormsComplete = useMemo(() => {
    if (!householdMembers?.length) {
      return false;
    }

    let filteredHouseholdMembers = householdMembers;
    if (hasAdoptedHotma) {
      filteredHouseholdMembers = filterOutFosterChildAdults(householdMembers);
    }

    return filteredHouseholdMembers.every((member) => {
      const {
        completedIncomeForm,
        completedAssetForm,
        completedRecertForm = false,
      } = member;

      return (
        showRecertForm
          ? [completedIncomeForm, completedAssetForm, completedRecertForm]
          : [completedIncomeForm, completedAssetForm]
      ).every((isComplete) => isComplete);
    });
  }, [householdMembers, showRecertForm, hasAdoptedHotma]);

  const {
    hideUI,
    isInitialCertForNonHUD,
    isRecertification,
    isReopenOrCorrection,
    needsMoveInValues,
    recertDueDate,
    recertsIncomplete,
    hasSetAsideProgramAssigned,
    selectedProgram,
  } = useMemo(() => {
    const formattedSetup = getSettingsByPropertyAndProgram(
      affordableSetup,
      programName,
    );
    const requiresInitialCertification =
      initialCertsForLihtc &&
      (formattedSetup?.requiresInitialCertification ?? false);
    const isNotHUDProgram = programName !== HUD_PROGRAM_NAME;
    const isInitialCertForNonHUD =
      [INITIAL_CERT_TYPE, INITIAL_UT_CERT_TYPE].includes(certificationType) &&
      isNotHUDProgram &&
      requiresInitialCertification;
    const isRecertification =
      certificationType === RECERT_CERT_TYPE ||
      (recertificationStarted ?? false);

    const isReopen = qualificationData?.certificationStatus === 'REOPEN';
    const isCorrection = qualificationData?.isCorrection;
    const isReopenOrCorrection = isReopen || isCorrection;

    const aqSetAsides = qualificationData?.setAsidePrograms || [];
    const hasSetAsideProgramAssigned = aqSetAsides?.some(
      (aqsa) =>
        !isEmpty(qualificationData?.qualificationTestResults?.[aqsa.id] || {}),
    );
    // For HUD, RD and PH, these programs will always have only one set aside program assigned.
    const selectedProgram =
      qualificationData?.qualificationTestResults?.[aqSetAsides?.[0]?.id];

    const hasCompletedCertifications = affordableQualifications?.some(
      (cert) =>
        cert.qualificationData?.certificationStatus ===
          AFFORDABLE_QUALIFICATION_STATUSES.COMPLETE &&
        [MOVE_IN_CERT_TYPE, INITIAL_CERT_TYPE].includes(
          cert.qualificationData?.certificationType,
        ),
    );

    const hasQualificationData = Boolean(qualificationData?.id);

    // Do not key hideUI off isFetching — background refetches after save must keep the cert UI visible.
    const hideUIVal =
      !hasQualificationData ||
      (isResident
        ? qualificationData?.isActive !== true &&
          qualificationData?.certificationType !== MOVE_IN_CERT_TYPE
        : hasCompletedCertifications && !qualificationData?.isActive);

    return {
      hideUI: hideUIVal,
      isInitialCertForNonHUD,
      isRecertification,
      isReopen,
      isReopenOrCorrection,
      needsMoveInValues:
        // NOTE: This values have nver lived on the affordable setup table, might be a bug over here.
        // Decided not to fix for now on the interest of time and introducing any new bug
        residentId &&
        (affordableSetup.HHincomeAtMoveIn === null ||
          affordableSetup.HHnumOccupantsAtMoveIn === null),
      hasSetAsideProgramAssigned,
      selectedProgram,
      recertDueDate: getRecertDueDate(recertDueDates, programName),
      recertsIncomplete:
        certificationType === MOVE_IN_CERT_TYPE
          ? false
          : !isHouseholdMembersChecklistFormsComplete,
    };
  }, [
    affordableSetup,
    programName,
    initialCertsForLihtc,
    certificationType,
    recertificationStarted,
    qualificationData?.certificationStatus,
    qualificationData?.isCorrection,
    qualificationData?.setAsidePrograms,
    qualificationData?.id,
    qualificationData?.isActive,
    qualificationData?.certificationType,
    qualificationData?.qualificationTestResults,
    affordableQualifications,
    isResident,
    residentId,
    recertDueDates,
    isHouseholdMembersChecklistFormsComplete,
  ]);

  const affordableQualificationsWithResolvedUnit = useMemo(() => {
    const list = affordableQualifications ?? [];
    const historyList = qualificationHistory ?? [];

    return list.map((aq) => {
      const currentUnitNumber = aq.certUnitNumber;
      const correctedQualId = aq.qualificationData?.correctedQualId;

      const hasEmptyUnitNumber =
        !currentUnitNumber || currentUnitNumber.trim() === '';

      if (!hasEmptyUnitNumber || !correctedQualId) {
        return aq;
      }

      const history = historyList.find(
        (h) =>
          h.aq?.id === correctedQualId ||
          h.affordableQualificationId === correctedQualId,
      );

      const historyUnitNumber =
        history?.aq?.tenantEvent?.tenantEventUnit?.unitNumber ?? '';
      const historyUnitId =
        history?.aq?.tenantEvent?.tenantEventUnit?.unitId ?? '';

      if (!historyUnitNumber && !historyUnitId) {
        return aq;
      }

      return {
        ...aq,
        certUnitNumber: currentUnitNumber || historyUnitNumber,
        unitId: aq.unitId || historyUnitId,
      };
    });
  }, [affordableQualifications, qualificationHistory]);

  return {
    affordableQualifications: affordableQualificationsWithResolvedUnit,
    affordableDocumentIds,
    affordableQualificationId,
    affordableQualificationCorrections,
    affordableSetup,
    certificationNotes,
    certificationStatus,
    certificationType,
    complianceApprovalId,
    complianceNotes,
    correctionReasonIds,
    assistanceDetails,
    backgroundAtAdmission,
    exemptions,
    familySelfSufficiency,
    handleDownloadDocument,
    hasGeneratedTIC,
    hasGeneratedHUD58,
    hasUnit,
    hideUI,
    homeRecertTypeId,
    householdAffordableDetails,
    householdMoveInDate,
    householdMembers,
    householdMembersWithMinors,
    householdPhaseInReliefs,
    isInitialCertForNonHUD,
    isRecertification,
    isReopen: qualificationData?.certificationStatus === 'REOPEN',
    isReopenOrCorrection,
    isAffordableMixedProperty,
    isHouseholdMembersChecklistFormsComplete,
    isLoading: isLoading || isLoadingHistory,
    isModification,
    isCorrection: qualificationData?.isCorrection ?? false,
    isActiveCertification: qualificationData?.isActive === true,
    isResident,
    qualificationUnit: qualificationUnitResolved,
    leasedRent: rentAtDate,
    limitRecertStart,
    masterAffordableProgramId,
    needsMoveInValues,
    organizationId,
    previousAssistanceDetails,
    propertyId,
    qualificationData,
    isTestHouseholdCurrent,
    qualificationOptions,
    recertDueDate,
    reCompleteCertificationStarted,
    recertificationStarted,
    recertsIncomplete,
    refetch,
    residentHouseholdMembers,
    showRecertForm,
    hasSetAsideProgramAssigned,
    selectedProgram,
    selectedProperty: resolvedProperty,
    specialNeedsDesignationIds,
    voucherEffectiveDate,
    stateProgramConfigs,
    canStartInitialCertification,
    completedAffordableQualifications,
    isFetching,
  };
};

export const useFetchStateProgramHotmaStatus = ({
  programName,
  state,
  effectiveDate,
  options = {},
}: {
  programName: string,
  state: string,
  effectiveDate: string,
  options?: Object,
}) => {
  const stateAffordableProgramConfigService = useMemo(
    () => new StateAffordableProgramConfigService(),
    [],
  );
  const queryKey = [
    'useFetchStateProgramHotmaStatus',
    programName,
    state,
    effectiveDate,
  ];

  return useQuery(
    queryKey,
    () =>
      stateAffordableProgramConfigService.determineHotmaStatus({
        programName,
        state,
        effectiveDate,
      }),
    options,
  );
};

export const useCorrectionTypesOptionsQuery = ({
  organizationId,
  propertyId,
  programName,
}) => {
  const affordableService = useMemo(() => new AffordableService(), []);
  const options = useAbortController();
  const queryKey = ['getCorrectionCodes'];

  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      affordableService.getAffordableCorrectionCodes(
        organizationId,
        propertyId,
        programName,
        options,
      ),
    { refetchOnWindowFocus: false },
  );

  const memoizedData = useMemo(() => data ?? [], [data]);

  return {
    data: memoizedData,
    isLoading,
  };
};

export default useGeneralAffordable;
